import { useEffect, useState } from 'react';
import type {
  RegisterInstallationRequest,
  RotateKeyRequest,
  RevokeCompromisedKeyRequest,
  InstallationEnvironment,
  DeploymentProfile,
  RolloutRing,
} from '@hospasense/contracts';
import { Button, Modal, Input, Select } from '../components/ui/index.js';

interface RegisterInstallationModalProps {
  isOpen: boolean;
  onClose: () => void;
  organisations: Array<{ id: string; displayName: string }>;
  onSubmit: (data: RegisterInstallationRequest) => Promise<void>;
}

export function RegisterInstallationModal({
  isOpen,
  onClose,
  organisations,
  onSubmit,
}: RegisterInstallationModalProps) {
  const [organisationId, setOrganisationId] = useState(organisations[0]?.id ?? '');
  const [installationIdentifier, setInstallationIdentifier] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [environment, setEnvironment] = useState<InstallationEnvironment>('production');
  const [deploymentProfile, setDeploymentProfile] =
    useState<DeploymentProfile>('standalone_onprem');
  const [installedVersion, setInstalledVersion] = useState('v4.2.0');
  const [rolloutRing, setRolloutRing] = useState<RolloutRing>('general');
  const [signingSecret, setSigningSecret] = useState('');
  const [region, setRegion] = useState('in-south-1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (organisations.length > 0 && !organisationId) {
      setOrganisationId(organisations[0]!.id);
    }
  }, [organisations, organisationId]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const effectiveOrgId =
        organisationId || (organisations.length > 0 ? organisations[0]!.id : '');
      if (!effectiveOrgId) {
        throw new Error('Please select an organisation.');
      }

      await onSubmit({
        organisationId: effectiveOrgId,
        installationIdentifier,
        displayName,
        environment,
        deploymentProfile,
        installedVersion,
        rolloutRing,
        signingSecret,
        region,
        integrationReadiness: {
          abdmBridge: false,
          pacsDicom: false,
          limsInterface: true,
          smsGateway: true,
        },
      });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={isOpen} title="Register Fleet Installation" onClose={onClose} wide>
      {error && <div className="mb-3 rounded-lg bg-error-bg p-3 text-xs text-error">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-3" data-testid="register-installation-form">
        <Select
          label="Organisation"
          value={organisationId || (organisations[0]?.id ?? '')}
          onChange={(e) => setOrganisationId(e.target.value)}
          required
        >
          {organisations.map((org) => (
            <option key={org.id} value={org.id}>
              {org.displayName}
            </option>
          ))}
        </Select>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Identifier"
            value={installationIdentifier}
            onChange={(e) => setInstallationIdentifier(e.target.value)}
            placeholder="inst-cloud-mum-01"
            required
          />
          <Input
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Mumbai Secondary Cloud Node"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Deployment Profile"
            value={deploymentProfile}
            onChange={(e) => setDeploymentProfile(e.target.value as DeploymentProfile)}
          >
            <option value="standalone_onprem">Standalone On-Prem</option>
            <option value="ha_cluster_onprem">HA Cluster On-Prem</option>
            <option value="cloud_managed_single_tenant">Cloud Single-Tenant</option>
            <option value="cloud_multi_tenant">Cloud Multi-Tenant</option>
          </Select>
          <Select
            label="Rollout Ring"
            value={rolloutRing}
            onChange={(e) => setRolloutRing(e.target.value as RolloutRing)}
          >
            <option value="internal">Internal (Canary)</option>
            <option value="pilot">Pilot Ring</option>
            <option value="general">General Production</option>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Installed Version"
            value={installedVersion}
            onChange={(e) => setInstalledVersion(e.target.value)}
            required
          />
          <Select
            label="Environment"
            value={environment}
            onChange={(e) => setEnvironment(e.target.value as InstallationEnvironment)}
          >
            <option value="production">Production</option>
            <option value="pilot">Pilot</option>
            <option value="staging">Staging</option>
            <option value="dr">DR Standby</option>
          </Select>
        </div>

        <Input label="Region" value={region} onChange={(e) => setRegion(e.target.value)} required />

        <Input
          label="Signing Secret (Min 16 chars)"
          type="password"
          value={signingSecret}
          onChange={(e) => setSigningSecret(e.target.value)}
          placeholder="secret-token-key-16"
          data-testid="signing-secret-input"
          required
          minLength={16}
        />

        <div className="mt-6 flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={submitting}
            data-testid="submit-registration-btn"
          >
            Complete Registration
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function RotateKeyModal({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: RotateKeyRequest) => Promise<void>;
}) {
  const [newSecret, setNewSecret] = useState('');
  const [reason, setReason] = useState('Scheduled quarterly key rotation');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({ algorithm: 'hmac-sha256', newSecretOrKey: newSecret, reason });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={isOpen} title="Rotate Signing Key" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          label="New Secret (Min 16 chars)"
          type="password"
          value={newSecret}
          onChange={(e) => setNewSecret(e.target.value)}
          placeholder="e.g. key-2026-q4"
          required
          minLength={16}
        />
        <Input
          label="Audit Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={submitting}>
            Confirm Rotation
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function RevokeKeyModal({
  isOpen,
  keyId,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  keyId: string;
  onClose: () => void;
  onSubmit: (data: RevokeCompromisedKeyRequest) => Promise<void>;
}) {
  const [reason, setReason] = useState('Suspected key compromise or leak');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({ keyId, reason });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={isOpen} title="Revoke Compromised Key" onClose={onClose}>
      <p className="text-xs text-text-secondary">
        This will immediately invalidate key <code className="text-error font-mono">{keyId}</code>{' '}
        and mark the installation offline until a new key is rotated.
      </p>
      <form onSubmit={handleSubmit} className="mt-4 space-y-3">
        <Input
          label="Revocation Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" type="submit" loading={submitting}>
            Confirm Revoke
          </Button>
        </div>
      </form>
    </Modal>
  );
}
